import { Box, lighten, Typography } from "@mui/material";
import { Location1Icon, MailIcon, PhoneIcon } from "@/components/common/icons";
import IconCircleContainer from "@/components/common/icons/containers/iconCircleContainer";
import { MedspaInfoQuery } from "@/graphql/queries/medspaInfo.graphql.types";
import { getAddressString } from "@/utils";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";

export type MedspaContactInfoProps = {
  name: string;
  customizationPrimaryColor: string;
  phone?: string;
  email?: string;
  address?: MedspaInfoQuery["medspa"][number]["address"];
};

export default function MedspaContactInfo({
  name,
  phone,
  email,
  address,
  customizationPrimaryColor,
}: MedspaContactInfoProps) {
  const addressString = getAddressString(address);

  return (
    <Box
      sx={{
        my: 5,
      }}
    >
      <Typography
        variant="subtitleDefault"
        sx={{
          mb: 2,
        }}
      >
        Contact details
      </Typography>
      <Box
        sx={{
          flexDirection: "row",
          display: "flex",
          flexWrap: "wrap",
          columnGap: 8,
          rowGap: 1.5,
        }}
      >
        {phone && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <IconCircleContainer
              size="36px"
              color={lighten(customizationPrimaryColor, 0.85)}
              icon={<PhoneIcon size="20px" color={customizationPrimaryColor} />}
            />
            <Typography
              component="a"
              variant="paragraphSmall"
              href={`tel:${phone}`}
              aria-label={`Phone to ${name}`}
              style={{ wordBreak: "break-word", textDecoration: "underline" }}
              sx={{
                color: customizationPrimaryColor,
              }}
            >
              {formatPhoneNumber(phone)}
            </Typography>
          </Box>
        )}
        {email && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <IconCircleContainer
              size="36px"
              color={lighten(customizationPrimaryColor, 0.85)}
              icon={<MailIcon size="20px" color={customizationPrimaryColor} />}
            />
            <Typography
              component="a"
              variant="paragraphSmall"
              href={`mailto:${email}`}
              aria-label={`Mail to ${name}`}
              style={{ wordBreak: "break-word", textDecoration: "underline" }}
              sx={{
                color: customizationPrimaryColor,
              }}
            >
              {email}
            </Typography>
          </Box>
        )}
        {addressString && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            <IconCircleContainer
              size="36px"
              color={lighten(customizationPrimaryColor, 0.85)}
              icon={
                <Location1Icon size="20px" color={customizationPrimaryColor} />
              }
            />
            <Typography variant="paragraphSmall">{addressString}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
